/**
 * The last alert check, per portfolio, for the nav badge.
 *
 * Performance Summary checks the rules whenever its book or prices change (on
 * load, and again when a live refresh lands) and publishes the outcomes here;
 * the badge beside the Summary tab reads them on every page. In memory only:
 * the rules live in settings and the outcomes are recomputed from the book,
 * so a reload simply checks again — nothing here can go stale across devices.
 */
import type { AlertOutcome } from './alertRules';

export interface AlertCheck {
  /** When the check ran (ms since epoch). */
  at: number;
  outcomes: AlertOutcome[];
}

const checks = new Map<string, AlertCheck>();
const listeners = new Set<() => void>();

export function publishAlertCheck(portfolioId: string, check: AlertCheck): void {
  checks.set(portfolioId, check);
  for (const l of listeners) l();
}

export function getAlertCheck(portfolioId: string): AlertCheck | null {
  return checks.get(portfolioId) ?? null;
}

export function subscribeAlertChecks(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function firedCount(check: AlertCheck | null): number {
  return check ? check.outcomes.filter((o) => o.status === 'fired').length : 0;
}

/** Tests only. */
export function resetAlertChecks(): void {
  checks.clear();
  for (const l of listeners) l();
}
